import {
  NUpPdfError,
  type NUpOptions,
  type NUpResult,
} from './lib'

interface WorkerRequest {
  id: number
  file: File
  options: NUpOptions
}

interface WorkerResponse {
  id: number
  bytes?: Uint8Array
  sourcePages?: number
  outputSheets?: number
  dimensions?: NUpResult['dimensions']
  error?: NUpPdfError['code']
}

let nextRequestId = 0

export async function exportNUpPdf(
  file: File,
  options: NUpOptions,
  signal?: AbortSignal,
): Promise<NUpResult> {
  if (typeof Worker === 'undefined') throw new NUpPdfError('invalid_input')

  const worker = new Worker(new URL('./n-up.worker.ts', import.meta.url), { type: 'module' })
  const id = ++nextRequestId

  return await new Promise<NUpResult>((resolve, reject) => {
    let settled = false

    const cleanup = () => {
      signal?.removeEventListener('abort', cancel)
      worker.onmessage = null
      worker.onerror = null
      worker.terminate()
    }
    const settle = (callback: () => void) => {
      if (settled) return
      settled = true
      cleanup()
      callback()
    }
    const cancel = () => {
      settle(() => reject(new DOMException('Cancelled', 'AbortError')))
    }

    if (signal?.aborted) {
      cancel()
      return
    }

    signal?.addEventListener('abort', cancel, { once: true })
    worker.onerror = () => {
      settle(() => reject(new NUpPdfError('parse_error')))
    }
    worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      if (event.data.id !== id) return
      if (
        event.data.error
        || !event.data.bytes
        || event.data.sourcePages === undefined
        || event.data.outputSheets === undefined
        || !event.data.dimensions
      ) {
        settle(() => reject(new NUpPdfError(event.data.error ?? 'parse_error')))
        return
      }

      settle(() => resolve({
        bytes: event.data.bytes!,
        sourcePages: event.data.sourcePages!,
        outputSheets: event.data.outputSheets!,
        dimensions: event.data.dimensions!,
      }))
    }

    const request: WorkerRequest = { id, file, options }
    worker.postMessage(request)
  })
}
